import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { parse } from "date-fns";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4themes_animated from "@amcharts/amcharts4/themes/animated";
import "./DynamicTrendGraph.css";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';
import ChatBotWindow from "../../ChatBot/ChatBotWindow";
import { useChat } from '../../Layout/ChatContext';
import ErrorModal from "../../Security/ErrorModal";

am4core.useTheme(am4themes_animated.default);

const DynamicTrendGraph = () => {
  const { t, i18n } = useTranslation();
  const userData = useSelector((state) => state.userData);
  const token = localStorage.getItem("AccessToken");
  const navigate = useNavigate();
  const {isChatVisible, toggleChat, showChat } = useChat();
  const [modalMessage, setModalMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [reloadKey, setReloadKey] = useState(0);

  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const deviceType = "PatientPortal";

  useEffect(() => {
    const params = { AppUserID: userData?.userData?.AppUserId, DeviceType: deviceType };
    async function fetchData() {
      try {
        const response = await axios.post(
          Endpoints.LoadPatientVitals,
          `_Params=${encodeURIComponent(JSON.stringify(params))}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const validData = response.data
          .filter(
            (item) =>
              item.SelectedDate &&
              (item.BloodPressure ||
              item.Glucose ||
              item.Weightinlbs ||
              item.Weightinkgs ||
              item.heartRate ||
              item.sleep ||
              item.temperature)
          )
          .map((item) => ({
            date: parse(item.SelectedDate, "MM/dd/yyyy", new Date()),
            bloodPressure: item.BloodPressure ? parseInt(item.BloodPressure.split("/")[0]) || 0 : 0,
            glucose: parseInt(item.Glucose) || 0,
            weight: parseFloat(item.Weightinlbs) || (item.Weightinkgs ? parseFloat(item.Weightinkgs) * 2.20462 : 0),
            heartRate: parseInt(item.heartRate) || 0,
            sleep: parseInt(item.sleep) || 0,
            temperature: parseFloat(item.temperature) || 0,
          }))
          .sort((a, b) => a.date - b.date);

        if (validData.length > 0) {
          setNoData(false);
          initializeChart(validData);
        } else {
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setNoData(true);
      } finally {
        setLoading(false);
      }
    }

    function initializeChart(data) {
      const chart = am4core.create("chartdiv", am4charts.XYChart);
      chart.paddingRight = 40;
      chart.paddingLeft = 40;
      chart.data = data;

      chart.logo.disabled = true;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.baseInterval = { timeUnit: "day", count: 1 };
      dateAxis.renderer.minGridDistance = 50;
      dateAxis.tooltipDateFormat = "MM/dd/yyyy";

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;

      function createSeries(field, name, color) {
        const series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = field;
        series.dataFields.dateX = "date";
        series.name = name;
        series.stroke = am4core.color(color);
        series.tooltipText = `${name}: [bold]{valueY}[/]`;
        series.strokeWidth = 2;
        series.tensionX = 2;
        series.fillOpacity = 0;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 4;
        bullet.circle.fill = am4core.color("#fff");
        bullet.circle.strokeWidth = 2;
        bullet.circle.stroke = am4core.color(color);

        return series;
      }

      createSeries("bloodPressure", t("BloodPressurevital"), "#00ff00");
      createSeries("glucose", t("Glucose"), "#ff007f");
      createSeries("weight", t("Weight"), "#0000ff");
      createSeries("heartRate", t("HeartRate"), "#ff0000");
      createSeries("sleep", t("Sleep"), "#800080");
      createSeries("temperature", t("Temperature"), "#ffa500");

      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineX.strokeWidth = 2;
      chart.cursor.lineY.strokeWidth = 0;
      chart.cursor.xAxis = dateAxis;

      chart.scrollbarX = new am4core.Scrollbar();
      chart.legend = new am4charts.Legend();
      chart.legend.position = "top";

      return () => {
        chart.dispose();
      };
    }

    fetchData();

    return () => {
      if (am4core && am4core.disposeAllCharts) am4core.disposeAllCharts();
    };
  }, [userData, token, reloadKey]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = {
       EmailID: userData?.userData?.EmailID,
        MemberId : userData?.userData?.MemberID ,
        Contract: userData?.userData?.Contract || userData?.userData?.ContractIdentified,
        IsExportType:"Pregnancy"
      };
  
      const response = await axios.post(
        Endpoints.ExportPregnencyTrendReport,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.Result = 1) {
        setModalMessage("Report exported successfully.");
      } else {
        setModalMessage(response.data || "Something went wrong while exporting.");
      }
    } catch (error) {
      console.error("Export error:", error);
      setModalMessage("An error occurred while exporting the report.");
    }
    finally {
      setLoading(false);
      setShowModal(true);
       setReloadKey(prev => prev + 1);
  };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>        
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }
  return (
    <>

{isHighmarkPortal ? 
          <div className="col-md-6 mt-4 d-flex inner-page-heading-wrapper">
          <button className="win-back-btn" onClick={handleBackClick}>
              <span>
              <i className="bi bi-chevron-left"></i>
              </span>
          </button>       
          <h2 className="page-heading">{t('VitalTrends')}</h2>
          </div>:
          <div className="col-md-6 mt-4 d-flex inner-page-heading-wrapper">
          <button className="win-back-btn" onClick={handleBackClick}>
              <span>
              <i className="bi bi-chevron-left"></i>    
              {t('Back')}        
              </span>
          </button>       
          <h2 className="page-heading">{t('VitalTrends')}</h2>
          </div>
      }
      <div>
        
       {noData ? (
          <p>{t("nodatamessage")}</p>
        ) : (
          <>
          <div className="note-gray-bold-16 ms-5">{t("trends")}</div>
          <div id="chartdiv" style={{ height: "500px" }}></div>
          </>    
        )}
      </div>
      <div className="col-12 d-flex justify-content-center">
          {noData? false:<button className="win-btn-primary mobile-view mt-4 mt-md-0 mb-5" onClick={handleExport}>
        {t("Exportbtn")}
        </button>} 
        </div>

        <ErrorModal
  show={showModal}
  message={modalMessage}
  onClose={() => setShowModal(false)}
/>
    </>
  );
};

export default DynamicTrendGraph;
